import { storeToRefs } from "pinia"
import { useConfigStore } from "@/stores/config"
import Bugsnag from "@bugsnag/js"
import axios, {
  AxiosError,
  type AxiosPromise,
  type AxiosRequestConfig,
} from "axios"
import { formatEndpoint, setDB, getDB } from "@/plugins/mockDataRecorder"
import { getShouldNotifyBugSnag } from "@/helpers/bugsnag"

axios.defaults.headers.common = {
  Accept: "application/json",
  "Content-Type": "application/json",
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Auth redirect
    if (error?.response?.status === 401) {
      const configStore = useConfigStore()
      document.location.href = configStore.urls.login
      return Promise.reject(error)
    }

    // Rate limiting
    if (error?.response?.status === 429) {
      console.warn("429 Too Many Attempts")
      window.alert("Take your time, the server feels under attack 😅")
    }

    return Promise.reject(error)
  }
)

function makeRequest<T>(options: AxiosRequestConfig): AxiosPromise<T> {
  const { urls } = storeToRefs(useConfigStore())

  const baseUrl = options.baseURL || urls.value.api

  return axios({
    ...options,
    withCredentials: true,
    url: `${baseUrl}${options.url}`,
    validateStatus: (status) => status < 400,
  })
}

export default async function <T>(options: AxiosRequestConfig) {
  const configStore = useConfigStore()
  const { mockMode } = storeToRefs(configStore)

  // Mock reading
  if (mockMode.value === "read") {
    const endpoint = formatEndpoint(options)
    return await getDB(endpoint)
  }

  try {
    const res = await makeRequest<T>(options)

    // Mock recording
    if (mockMode.value === "record") {
      const endpoint = formatEndpoint(options)
      setDB(endpoint, res.data)
    }

    return res.data
  } catch (error) {
    // Check error type
    if (!(error instanceof Error)) {
      throw error
    }

    // Bugsnag notifications
    if (error instanceof AxiosError && error.response) {
      if (getShouldNotifyBugSnag(error.response)) {
        Bugsnag.notify(error, (event) => {
          event.addMetadata("errorResponse", error.response?.data)
          event.addMetadata("request", {
            payload: error.config?.data,
            url: error.config?.url,
            method: error.config?.method,
          })
        })
      }
    }

    throw error
  }
}
